/* eslint-disable react/no-array-index-key */
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import buildFilterString from '../../helpers/buildFilterString';
import { useCallback, useMemo, useState } from 'react';
import { getSalesOrders } from 'src/services/salesorder';
import { SalesOrderListDto } from 'src/models/salesorder';
import { ConsignmentMapProps, JobMarker, JobType } from './types';
import { useSettings } from 'src/@core/hooks/useSettings';

const OpenMap = dynamic(() => import('./OpenMap'), { ssr: false });
const MapMarker = dynamic(() => import('./OpenMap/MapMarker'), { ssr: false });

/**
 * Consignment data query
 */
const ConsignmentMap = ({ dashboardDateStrings = [[''], ['']], orderByValue = '' }: ConsignmentMapProps) => {
  const [type, setType] = useState<JobType>('delivery');

  const { settings } = useSettings();
  const theme = useTheme();

  const filterString = useMemo(
    () => buildFilterString(orderByValue, dashboardDateStrings),
    [orderByValue, dashboardDateStrings]
  );

  const { data: consignmentsData } = useQuery({
    queryKey: ['consignmentMapWidget', type, filterString],
    queryFn: async () => {
      const res = await getSalesOrders({
        filter: `${filterString} and deliveryType eq ${type}`
      });

      return res.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000
  });

  const handleChange = (event: SelectChangeEvent) => {
    setType(event.target.value as JobType);
  };

  const buildLatLong = useCallback((job: SalesOrderListDto, delType: JobType) => {
    if (delType === 'collection') {
      return [job?.collectionAddressLatitude ?? 0, job.collectionAddressLongitude ?? 0];
    }

    return [job?.deliveryAddressLatitude ?? 0, job.deliveryAddressLongitude ?? 0];
  }, []);

  const mapMarkers: JobMarker[] = useMemo(() => {
    // job delivery type is 'third pary' then need a marker for both collection and delivery
    if (type === 'third party') {
      const res: JobMarker[] = [];

      consignmentsData?.forEach((consignment: any) => {
        res.push({
          latLong: buildLatLong(consignment, 'collection'),
          conNumber: consignment?.consignment,
          deliveryDate: consignment?.deliveryDateTime,
          collectionDate: consignment?.collectionDateTime,
          deliveryType: 'collection',
          latestStatus: consignment?.latestStatus,
          id: consignment?.id
        });

        res.push({
          latLong: buildLatLong(consignment, 'delivery'),
          conNumber: consignment?.consignment,
          deliveryDate: consignment?.deliveryDateTime,
          collectionDate: consignment?.collectionDateTime,
          deliveryType: 'delivery',
          latestStatus: consignment?.latestStatus,
          id: consignment?.id
        });
      });

      return res;
    }

    return (
      consignmentsData
        ?.filter(consignment => consignment?.deliveryType?.toLowerCase() === type)
        ?.map((consignment: any) => {
          return {
            latLong: buildLatLong(consignment, consignment?.deliveryType?.toLowerCase() as JobType),
            conNumber: consignment?.consignment,
            deliveryDate: consignment?.deliveryDateTime,
            collectionDate: consignment?.collectionDateTime,
            deliveryType: consignment?.deliveryType,
            latestStatus: consignment?.latestStatus,
            id: consignment?.id,
            icon: MapMarker
          };
        }) ?? []
    );
  }, [buildLatLong, consignmentsData, type]);

  return (
    <Box
      sx={{ height: '100%', position: 'relative' }}
      className={theme.palette.mode === 'dark' ? 'darkConsignmentMap' : ''}>
      {!settings.unlockDashboardLayout && (
        <FormControl
          sx={{ m: 1, minWidth: 120, position: 'absolute', right: 5, top: 5, zIndex: '9999999' }}
          size="small">
          <Select
            id="del-type-select"
            value={type}
            onChange={handleChange}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.6)',
              color: 'rgba(0,0,0,0.5)',
              padding: '0px',
              transition: 'background-color 0.1s ease-in, color 0.1s ease-in',
              '&:hover': { backgroundColor: 'rgba(255,255,255,1)', color: 'rgba(0,0,0,1)' },
              '&:focus': { borderColor: 'none', outline: 'none' }
            }}>
            <MenuItem value={'delivery'}>Delivery</MenuItem>
            <MenuItem value={'collection'}>Collection</MenuItem>
            {/* <MenuItem value={'third party'}>Third Party</MenuItem> */}
          </Select>
        </FormControl>
      )}
      <OpenMap
        center={[52.35, -2.04]}
        zoom={6}
        scrollWheelZoom={true}
        style={{
          width: '100%',
          boxShadow: '0px 3px 10px -1px rgba(0,0,0,0.5)'
        }}>
        {mapMarkers.map((job, index) => {
          return <MapMarker key={index} data={job} />;
        })}
      </OpenMap>
    </Box>
  );
};

export default ConsignmentMap;
